import {Tooltip} from '@mui/material';
import React from 'react';

import {expiryLabel, TenantChip} from '../lib/aggregate';
import {aiAccountLabel, isAiAccount} from '../lib/ai-accounts';
import {chipInternalClass, chipKindClass} from '../lib/chip-style';
import {classifyImpAiSubject, formatImpAiSubject} from '../lib/imp-ai-subject';

export type ChipVariant = 'chips' | 'compact' | 'minimal';

interface Props {
    chip: TenantChip;
    variant?: ChipVariant;
    onClick?: (chip: TenantChip) => void;
    /** Drop the expiry badge — used when the expiry is shown in a separate column. */
    hideBadge?: boolean;
}

function badgeLabel(chip: TenantChip): string {
    if (chip.permanent) {
        return '∞';
    }
    if (chip.daysRemaining < 0) {
        return 'exp';
    }
    // < 1 day: show hours instead, e.g. "5h"
    if (chip.daysRemaining === 0 && chip.hoursRemaining >= 0 && chip.hoursRemaining < 24) {
        return `${chip.hoursRemaining}h`;
    }
    return expiryLabel(chip.daysRemaining);
}

function stateClass(chip: TenantChip): string {
    if (chip.permanent) {
        return 'permanent';
    }
    if (chip.daysRemaining < 0) {
        return 'expired';
    }
    // < 24 hours — its own colour tier (orange) above the red "expiring" tier.
    if (chip.daysRemaining === 0 && chip.hoursRemaining >= 0 && chip.hoursRemaining < 24) {
        return 'expiring-imminent';
    }
    if (chip.daysRemaining <= 7) {
        return 'expiring';
    }
    if (chip.daysRemaining <= 30) {
        return 'expiring-soon';
    }
    return '';
}

function tooltipText(chip: TenantChip): string {
    let head: string;
    if (chip.resource === '*') {
        head = chip.kind === 'imp-ai' ? 'Everyone (wildcard)' : 'All tenants (wildcard)';
    } else if (chip.kind === 'imp-ai') {
        const subject = classifyImpAiSubject(chip.resource);
        head =
            subject?.kind === 'domain'
                ? `Every user at ${subject.value}`
                : subject?.kind === 'email'
                  ? subject.value
                  : `${chip.resource} — not a valid subject, revoke this`;
    } else if (isAiAccount(chip.resource)) {
        head = `${aiAccountLabel(chip.resource)} · ${chip.resource} · internal Ennabl AI account`;
    } else if (chip.resourceName) {
        // Show name + every raw id that merged into this chip, so an admin sees
        // both the shard uid and the AWS account id when the grant came in via
        // both forms.
        head = `${chip.resourceName} · ${chip.resourceIds.join(', ')}`;
    } else {
        head = chip.resourceIds.join(', ') || chip.resource;
    }
    if (chip.permanent) {
        return `${head} · permanent`;
    }
    if (chip.daysRemaining < 0) {
        return `${head} · expired`;
    }
    if (chip.daysRemaining === 0 && chip.hoursRemaining >= 0 && chip.hoursRemaining < 24) {
        return `${head} · ${chip.hoursRemaining}h remaining`;
    }
    return `${head} · ${chip.daysRemaining}d remaining`;
}

export function AccessChip({chip, variant = 'chips', onClick, hideBadge}: Props) {
    const isWildcard = chip.resource === '*';
    const kindCls = chipKindClass(chip.kind);
    const internalCls = chipInternalClass(chip.kind, chip.resource);
    // A stored Ennabl AI subject that no longer classifies — only reachable if a
    // row got in around the route (a hand-written DynamoDB put, a bad migration).
    // It authorizes nothing, because the gate runs the same classifier; marking it
    // makes it obvious WITHOUT hovering, so it can be found and revoked.
    const invalidCls =
        chip.kind === 'imp-ai' && !isWildcard && classifyImpAiSubject(chip.resource) === null
            ? 'invalid'
            : '';
    const variantCls = variant === 'compact' ? 'compact' : variant === 'minimal' ? 'minimal' : '';
    const bastionCls = chip.fromBastion ? 's-bastion' : '';
    const stateCls = stateClass(chip);
    const wildcardCls = isWildcard ? 'wildcard' : '';
    const staticCls = onClick ? '' : 'static';
    const className = [
        'ac',
        kindCls,
        internalCls,
        invalidCls,
        variantCls,
        bastionCls,
        stateCls,
        wildcardCls,
        staticCls,
    ]
        .filter(Boolean)
        .join(' ');
    // An AI account has no shard config entry, so `resourceName` is undefined and
    // the raw 12-digit id would show. Prefer its alias.
    const display = isWildcard
        ? 'ALL'
        : // An Ennabl AI subject is an email or a domain, not a tenant — render the
          // LEVEL (`*@som.com` vs `some@som.com`) so an auditor scanning the column
          // can tell a whole-domain grant from a single-user one at a glance.
          chip.kind === 'imp-ai'
          ? formatImpAiSubject(chip.resource)
          : isAiAccount(chip.resource)
            ? aiAccountLabel(chip.resource)
            : chip.resourceName ?? chip.resource;
    const interactive = onClick
        ? {
              role: 'button' as const,
              tabIndex: 0,
              onClick: (e: React.MouseEvent) => {
                  e.stopPropagation();
                  onClick(chip);
              },
              onKeyDown: (e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      onClick(chip);
                  }
              },
          }
        : {};

    return (
        <Tooltip title={tooltipText(chip)} placement="top" arrow>
            <span
                className={className}
                {...interactive}
                data-chip-kind={chip.kind}
                data-chip-resource={chip.resource}
            >
                <span className="name">{display}</span>
                {!hideBadge && <span className="badge">{badgeLabel(chip)}</span>}
            </span>
        </Tooltip>
    );
}
